import { makeStyles, Typography } from '@paintscout/ui'
import { EstimatorAvatar, useClientOptions, useUsers } from '@paintscout/ui-business'
import type { CalendarEvent } from 'paintscout'
import React from 'react'
import { getCrews } from '@paintscout/util/builder'
import { DayEvent } from './DayEvent'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
    borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
  },
  managerName: {
    lineHeight: 1.2,
    fontWeight: 500
  },
  countBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    minWidth: '28px',
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.borderRadius.lg,
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
    color: theme.paintscout.palette.brand.Neutral.P60,
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 500,
    lineHeight: 1,
    textAlign: 'center'
  },
  events: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1, 1),
      gap: theme.spacing(1)
    }
  }
}))

interface ProjectsByManagerProps {
  events: CalendarEvent[]
}

export function ProjectsByManager({ events }: ProjectsByManagerProps) {
  const classes = useStyles()
  const { users, userPictures } = useUsers()
  const { options } = useClientOptions()
  const crews = getCrews({ options })
  const crew = crews.find((c) => c._id === events[0].crew)
  const projectManager = users.find((u) => u.user_id === crew?.projectManager?.id)

  // order events by total duration ascending
  const orderedEvents = events.sort((a, b) => {
    const aDuration = (a.end?.getTime() || 0) - (a.start?.getTime() || 0)
    const bDuration = (b.end?.getTime() || 0) - (b.start?.getTime() || 0)
    return aDuration - bDuration
  })

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <EstimatorAvatar
          user={{
            id: projectManager?.user_id,
            firstName: projectManager?.user_metadata?.firstName,
            lastName: projectManager?.user_metadata?.lastName
          }}
          userPictures={userPictures}
          borderColor={projectManager?.user_metadata?.color}
          width={'24px'}
          height={'24px'}
        />
        <Typography className={classes.managerName}>{projectManager?.name}</Typography>
        <span className={classes.countBadge}>{orderedEvents.length}</span>
      </div>
      <div className={classes.events}>
        {orderedEvents.map((event) => (
          <DayEvent event={event} key={event.id} />
        ))}
      </div>
    </div>
  )
}
